import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, MapPin, Clock, Award, Users, Target, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  const values = [
    {
      icon: Award,
      title: 'Quality First',
      description: 'We never compromise on quality. Every print job goes through rigorous quality checks before delivery.',
    },
    {
      icon: Zap,
      title: 'Fast Turnaround',
      description: 'Time-sensitive project? We offer same-day and rush services to meet your deadlines.',
    },
    {
      icon: Users,
      title: 'Customer Focus',
      description: 'Your satisfaction is our priority. We work closely with you to ensure perfect results.',
    },
    {
      icon: Target,
      title: 'Precision',
      description: 'State-of-the-art equipment and expert craftspeople ensure precise, consistent output.',
    },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-white paper-texture" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium tracking-widest uppercase text-[#3EACDB]">About Us</span>
              <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tighter uppercase mt-4">
                Your Trusted<br />
                <span className="text-[#3EACDB]">NYC</span> Print Partner
              </h1>
              <p className="mt-6 text-lg text-zinc-600 leading-relaxed">
                Charles Print has been serving New York City businesses with premium printing services. Located in the heart of Midtown Manhattan, we combine cutting-edge technology with personalized service to deliver exceptional results.
              </p>
              <div className="mt-8 flex flex-wrap gap-6">
                <div className="flex items-center gap-2 text-zinc-600">
                  <MapPin className="w-5 h-5 text-[#3EACDB]" />
                  <span>Midtown Manhattan</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-600">
                  <Clock className="w-5 h-5 text-[#3EACDB]" />
                  <span>Mon-Fri: 9AM - 5:30PM · Sat-Sun: By Appointment</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1561077728-73b4b06ca6fb?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="NYC skyline"
                className="w-full rounded-sm shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-zinc-900 text-white" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '15+', label: 'Years Experience' },
              { value: '10K+', label: 'Projects Completed' },
              { value: '500+', label: 'Happy Clients' },
              { value: '24hr', label: 'Rush Available' },
            ].map((stat, index) => (
              <div key={stat.label} data-testid={`stat-${index}`}>
                <p className="font-heading text-4xl md:text-5xl font-black text-[#3EACDB]">{stat.value}</p>
                <p className="mt-2 text-zinc-400 text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-24 bg-white" data-testid="our-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-sm font-medium tracking-widest uppercase text-[#3EACDB]">Our Story</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mt-4">
              Built on Quality, Driven by Service
            </h2>
            <div className="mt-8 text-zinc-600 space-y-6 text-left">
              <p>
                Charles Print was founded with a simple mission: provide New York City businesses with exceptional printing services that don't break the bank. What started as a small print shop has grown into a trusted partner for hundreds of businesses across Manhattan and beyond.
              </p>
              <p>
                Our team brings decades of combined experience in commercial printing. We've invested in state-of-the-art digital and offset printing equipment to ensure every job meets our exacting standards. From rush business card orders to complex marketing campaigns, we treat every project with the same level of care and attention.
              </p>
              <p>
                Located at 247 W 35th Street in the Garment District, we're easily accessible from anywhere in Manhattan. Drop by for a consultation, or give us a call—we're always happy to discuss your printing needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 md:py-24 bg-zinc-50" data-testid="our-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-sm font-medium tracking-widest uppercase text-[#3EACDB]">Our Values</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mt-4">
              What Sets Us Apart
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={value.title} 
                className="bg-white border border-zinc-200 p-8 hover:border-[#3EACDB]/50 transition-colors"
                data-testid={`value-${index}`}
              >
                <div className="w-12 h-12 bg-[#3EACDB]/10 rounded-sm flex items-center justify-center mb-6">
                  <value.icon className="w-6 h-6 text-[#3EACDB]" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-3">{value.title}</h3>
                <p className="text-zinc-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why NYC Businesses Choose Us */}
      <section className="py-16 md:py-24 bg-white" data-testid="why-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1569852741721-ee5a94bf719e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Printing press"
                className="w-full rounded-sm grayscale"
              />
            </div>
            <div>
              <span className="text-sm font-medium tracking-widest uppercase text-[#3EACDB]">Why Choose Us</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mt-4">
                The Charles Print Difference
              </h2>
              <ul className="mt-8 space-y-4">
                {[
                  'Convenient Midtown Manhattan location',
                  'State-of-the-art digital and offset equipment',
                  'Expert color matching and proofing',
                  'Competitive pricing with volume discounts',
                  'Same-day and rush services available',
                  'Free design consultation',
                  'Eco-friendly paper options',
                  'Reliable on-time delivery',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#3EACDB] mt-0.5 flex-shrink-0" />
                    <span className="text-zinc-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#3EACDB] text-white" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold">
            Ready to Work With Us?
          </h2>
          <p className="mt-4 text-blue-100 max-w-xl mx-auto">
            Visit our Midtown location or request a quote online. We're here to bring your print projects to life.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/quote" data-testid="about-cta-quote">
              <Button className="h-12 px-8 text-sm font-bold uppercase tracking-wider bg-white text-[#3EACDB] hover:bg-zinc-100 rounded-sm">
                Get a Quote
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact" data-testid="about-cta-contact">
              <Button variant="outline" className="h-12 px-8 text-sm font-bold uppercase tracking-wider rounded-sm border-2 border-white text-white hover:bg-white hover:text-[#3EACDB]">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
